import { useState } from "react";
import { ApplicationNavbar } from "@/ui/components/navigation";
import { ApplicationMenuModal } from "@/ui/components/common";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/domain/constants/routes";

/**
 * 專業助手（Agent）型別定義
 */
interface Agent {
  id: string;
  name: string;
  icon: string; // FontAwesome class
  description: string;
  features: string[];
  status: "available" | "coming";
  statusLabel: string;
  statusColor: string;
  link: () => void;
}

/**
 * 代理列表頁面（專業助手選擇頁）
 *
 * 參考 chat.html 架構，響應式卡片列表，支援桌機/行動裝置。
 * 點選卡片可進行後續導向（預留 onClick handler）。
 *
 * @returns {React.ReactElement}
 */
export function AgentListPage(): React.ReactElement {
  const navigate = useNavigate();
  /**
   * 顯示預約體驗提示，並在用戶確認後開啟 LINE 預約頁面。
   *
   * @returns {void}
   */
  function toBeImplemented(): void {
    if (window.confirm("即將推出，請至LINE預約搶先體驗！")) {
      window.open("https://lin.ee/1qx88DG", "_blank");
    }
  }

  /**
   * 專業助手列表資料（可根據實際需求擴充）
   */
  const agents: Agent[] = [
    {
      id: "website",
      name: "建站助手",
      icon: "fas fa-globe",
      description: "專業網站建置服務，規劃到上線一站式解決",
      features: ["響應式設計", "SEO優化", "內容管理"],
      status: "available",
      statusLabel: "可用",
      statusColor: "bg-green-500",
      link: () => navigate(ROUTES.CONVERSATION),
    },
    {
      id: "marketing",
      name: "行銷助手",
      icon: "fas fa-bullhorn",
      description: "數位行銷策略規劃，提升品牌知名度與銷售",
      features: ["社群媒體", "內容行銷", "廣告投放"],
      status: "coming",
      statusLabel: "即將推出",
      statusColor: "bg-amber-500",
      link: () => toBeImplemented(),
    },
    {
      id: "gmb",
      name: "GMB助手",
      icon: "fas fa-map-marker-alt",
      description: "Google 我的商家優化，提升在地搜尋排名",
      features: ["資訊優化", "評論管理", "在地曝光"],
      status: "coming",
      statusLabel: "即將推出",
      statusColor: "bg-amber-500",
      link: () => toBeImplemented(),
    },
    {
      id: "social",
      name: "社群經營助手",
      icon: "fas fa-hashtag",
      description: "內容排程、互動管理、IG/Thread 經營策略",
      features: ["內容排程", "互動管理", "成效分析"],
      status: "coming",
      statusLabel: "即將推出",
      statusColor: "bg-amber-500",
      link: () => toBeImplemented(),
    },
  ];

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 切換選單
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <ApplicationNavbar onMenuClick={toggleMenu} />

      {/* Welcome 區塊 */}
      <section className="max-w-7xl p-4 mx-auto md:p-6 mb-10">
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center space-x-2 bg-warm-beige border border-tea-brown/20 px-3 md:px-4 py-1 md:py-2 rounded-full text-xs md:text-sm mb-3 md:mb-4">
            <i className="fas fa-leaf text-earth-green"></i>
            <span className="text-tea-brown">智慧營運生態系統</span>
          </div>
          <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-earth-green mb-2 md:mb-4 handwriting">
            選擇您的專業助手
          </h1>
          <p className="text-md md:text-lg text-tea-brown font-medium leading-relaxed max-w-2xl mx-auto">
            點選助手卡片開始對話
            <br />
            享受專為台灣中小企業設計的智慧服務
          </p>
        </div>

        {/* 卡片列表區塊（mobile: stack, desktop: grid） */}
        <div className="space-y-2 md:space-y-0 desktop-assistants-grid mb-6">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className={
                "assistant-card desktop-assistant-card rounded-xl p-3 md:p-6 cursor-pointer no-select transition-all" +
                (agent.status === "available"
                  ? " hover:border-earth-green/60"
                  : " opacity-80")
              }
              onClick={() => agent.link()}
            >
              <div className="flex md:flex-col items-center md:items-start space-x-3 md:space-x-0 md:space-y-4">
                <div className="w-12 h-12 md:w-16 md:h-16 assistant-icon rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0">
                  <i
                    className={agent.icon + " text-lg md:text-2xl text-white"}
                  ></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1 md:mb-3">
                    <h3 className="text-md md:text-xl font-semibold font-medium text-earth-green handwriting">
                      {agent.name}
                    </h3>
                    <span
                      className={
                        agent.statusColor +
                        " text-white px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium"
                      }
                    >
                      {agent.statusLabel}
                    </span>
                  </div>
                  {/* 在手機版上只顯示一行, 多餘 ... */}
                  <p className="text-tea-brown text-md md:text-base font-medium leading-relaxed mb-0 md:mb-4 line-clamp-1 md:line-clamp-none">
                    {agent.description}
                  </p>
                  <div className="hidden md:flex flex-wrap gap-2">
                    {agent.features.map((feature) => (
                      <span
                        key={feature}
                        className="bg-earth-green/10 text-earth-green px-3 py-1 rounded-full text-md font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div
        id="bottom-nav"
        class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom z-40"
      >
        <div id="selection-footer" class="p-3 md:p-4">
          <div class="max-w-7xl mx-auto flex items-center justify-center space-x-4 md:space-x-8 text-xs md:text-sm text-tea-brown">
            <a
              href={ROUTES.HOME}
              class="flex items-center space-x-1 hover:text-earth-green transition-colors"
            >
              <i class="fas fa-home"></i>
              <span>首頁</span>
            </a>
            <div class="w-1 h-1 bg-tea-brown rounded-full"></div>
            <span class="flex items-center space-x-1">
              <i class="fas fa-shield-alt"></i>
              <span>安全認證</span>
            </span>
            <div class="w-1 h-1 bg-tea-brown rounded-full"></div>
            <span class="flex items-center space-x-1">
              <i class="fas fa-clock"></i>
              <span>24/7 服務</span>
            </span>
          </div>
        </div>
      </div>

      <ApplicationMenuModal
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={toggleMenu}
      />
    </>
  );
}
